package it.maicol07.gamerlogue.di

import at.released.igdbclient.IgdbClient
import at.released.igdbclient.ktor.IgdbKtorEngine
import io.ktor.client.HttpClient
import io.ktor.client.HttpClientConfig
import io.ktor.client.plugins.HttpRequestRetry
import io.ktor.client.plugins.HttpResponseValidator
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.ResponseException
import io.ktor.client.plugins.api.createClientPlugin
import io.ktor.client.plugins.auth.Auth
import io.ktor.client.plugins.auth.AuthCircuitBreaker
import io.ktor.client.plugins.auth.providers.bearer
import io.ktor.client.plugins.cache.HttpCache
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logger
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.request.accept
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.appendEncodedPathSegments
import io.ktor.http.contentType
import io.ktor.http.takeFrom
import io.ktor.serialization.kotlinx.json.json
import it.maicol07.gamerlogue.AppEnvironment
import it.maicol07.gamerlogue.BuildConfig
import it.maicol07.gamerlogue.auth.AuthTokenProvider
import it.maicol07.gamerlogue.auth.AuthenticationHandler
import it.maicol07.gamerlogue.auth.platformSession
import it.maicol07.gamerlogue.core.AppPreferences
import it.maicol07.gamerlogue.services.EpicApi
import it.maicol07.gamerlogue.services.PsnApi
import it.maicol07.gamerlogue.services.UbisoftApi
import it.maicol07.gamerlogue.services.XboxApi
import it.maicol07.spraypaintkt_ktor_integration.KtorHttpClient.Companion.VndApiJson
import kotlinx.serialization.json.Json
import org.koin.core.annotation.Configuration
import org.koin.core.annotation.Module
import org.koin.core.annotation.Named
import org.koin.core.annotation.Single
import org.koin.core.scope.Scope
import kotlin.coroutines.cancellation.CancellationException

/**
 * Shared across every client. Deliberately **without** `HttpCache`: Ktor keys the cache on the URL and
 * ignores the `Authorization` header, so on a per-user endpoint a response fetched under one token is
 * replayed under the next one (see `HttpCacheAuthTest`). Only the IGDB client, whose credentials are
 * the app's rather than the user's, installs it.
 */
private val ktorHttpClientConfig: HttpClientConfig<*>.() -> Unit = {
    install(Logging) {
        logger = object : Logger {
            override fun log(message: String) {
                co.touchlab.kermit.Logger.v(tag = "HTTP Client") { message }
            }
        }
        // HEADERS logs the Authorization header, so it stays out of anything but a local build.
        level = if (BuildConfig.APP_ENV == AppEnvironment.LOCAL) LogLevel.HEADERS else LogLevel.NONE
    }
    install(HttpTimeout) {
        requestTimeoutMillis = RequestTimeoutMillis
        connectTimeoutMillis = ConnectTimeoutMillis
        socketTimeoutMillis = SocketTimeoutMillis
    }
    install(HttpRequestRetry) {
        maxRetries = 3
        retryIf { _, response ->
            response.status.value in ServerErrorRange || response.status == HttpStatusCode.TooManyRequests
        }
        // Without this a dropped connection fails the call outright; cancellation must still propagate.
        retryOnExceptionIf { _, cause -> cause !is CancellationException }
        // Honours Retry-After, which is what makes the 429 branch above worth having.
        exponentialDelay()
    }
}

private val ServerErrorRange = 500..599

// Generous enough for a cold backend, short enough that a dead connection surfaces as an error
// instead of an indefinite spinner.
private const val RequestTimeoutMillis = 30_000L
private const val ConnectTimeoutMillis = 15_000L
private const val SocketTimeoutMillis = 30_000L

/**
 * `igdbclient` fixes its base URL at construction, but the client is a singleton and the endpoint can
 * change in the settings. It is built against this placeholder host, and every request is moved onto
 * the endpoint configured at the time it is sent.
 */
internal const val IGDB_PLACEHOLDER_HOST = "igdb.invalid"

internal fun igdbBaseUrl(currentBaseUrl: () -> String) = createClientPlugin("IgdbBaseUrl") {
    onRequest { request, _ ->
        if (request.url.host != IGDB_PLACEHOLDER_HOST) return@onRequest
        val endpoint = request.url.encodedPathSegments.filter(String::isNotEmpty)
        // takeFrom only replaces what the base URL specifies, so the query survives as is.
        request.url.takeFrom(currentBaseUrl()).appendEncodedPathSegments(endpoint)
    }
}

@Module
@Configuration
object HttpModule {
    /**
     * Provides an instance of [IgdbClient] configured with a custom Ktor HTTP engine and base URL.
     *
     * The client is initialized using the [IgdbKtorEngine]; requests go to [AppPreferences.igdbApiUrl],
     * read when each one is sent. A custom [HttpClient] is configured and provided to handle HTTP
     * communication, where additional settings can be applied through the `ktorHttpClientConfig` function.
     *
     * This method is annotated with `@Single` indicating it provides a singleton instance in the Koin dependency injection setup.
     *
     * @return A configured instance of [IgdbClient].
     */
    @Single
    fun provideIgdbClient(preferences: AppPreferences) = IgdbClient(IgdbKtorEngine) {
        baseUrl = "https://$IGDB_PLACEHOLDER_HOST"
        httpClient {
            this.httpClient = HttpClient {
                install(igdbBaseUrl { preferences.igdbApiUrl.value })
                // After igdbBaseUrl, so it sees the real endpoint rather than the placeholder host.
                install(platformSession { preferences.serverUrl.value })
                // Only appends to igdbclient's protobuf Accept, so error bodies come back as JSON.
                install(createClientPlugin("IgdbJsonErrors") {
                    onRequest { request, _ -> request.accept(ContentType.Application.Json) }
                })
                ktorHttpClientConfig()
                // Safe here, unlike on the user-scoped clients: IGDB responses are the same for every
                // user, so a URL-keyed cache cannot leak one user's data to another.
                install(HttpCache)
            }
        }
    }

    @Single
    @Named("JsonApiHttpClient")
    fun provideJsonApiHttpClient(
        authTokenProvider: AuthTokenProvider,
        authenticationHandler: AuthenticationHandler,
        scope: Scope,
    ) = HttpClient {
        defaultRequest {
            accept(VndApiJson)
            contentType(VndApiJson)
        }
        install(Logging) {
            logger = object : Logger {
                override fun log(message: String) {
                    co.touchlab.kermit.Logger.v(tag = "HTTP Client") { message }
                }
            }
            // HEADERS logs the Authorization header, so it stays out of anything but a local build.
            level = if (BuildConfig.APP_ENV == AppEnvironment.LOCAL) LogLevel.HEADERS else LogLevel.NONE
        }
        install(platformSession { scope.get<AppPreferences>().serverUrl.value })
        ktorHttpClientConfig()
        HttpResponseValidator {
            validateResponse { response ->
                val current = authTokenProvider.session.value
                val requestBearer = response.call.request.headers[HttpHeaders.Authorization]
                val bearerMatches = current.accessToken?.let { requestBearer == "Bearer $it" } == true
                val belongsToCurrentSession = bearerMatches ||
                    (current.isAuthenticated && current.accessToken == null)
                val refreshAlreadyTried = response.call.request.attributes.contains(AuthCircuitBreaker)
                if (response.status == HttpStatusCode.Unauthorized && belongsToCurrentSession &&
                    (current.refreshToken == null || refreshAlreadyTried)
                ) {
                    authTokenProvider.clearSession()
                }
            }
        }
        install(Auth) {
            bearer {
                cacheTokens = false
                nonCancellableRefresh = true
                loadTokens { authenticationHandler.loadBearerTokens() }
                refreshTokens {
                    val requestToken = response.call.request.headers[HttpHeaders.Authorization]
                        ?.removePrefix("Bearer ")
                    authenticationHandler.refreshBearerTokens(requestToken)
                }
            }
        }
    }

    @Single
    @Named("AuthHttpClient")
    fun provideAuthHttpClient(scope: Scope) = HttpClient {
        expectSuccess = true
        install(platformSession { scope.get<AppPreferences>().serverUrl.value })
        install(ContentNegotiation) {
            json(Json { ignoreUnknownKeys = true })
        }
        install(HttpTimeout) {
            requestTimeoutMillis = RequestTimeoutMillis
            connectTimeoutMillis = ConnectTimeoutMillis
            socketTimeoutMillis = SocketTimeoutMillis
        }
        install(HttpRequestRetry) {
            maxRetries = 1
            retryIf { _, response ->
                (response.status == HttpStatusCode.TooManyRequests).also { retry ->
                    if (retry) co.touchlab.kermit.Logger.w { "Token endpoint rate limited; retrying after Retry-After" }
                }
            }
            retryOnExceptionIf { request, cause ->
                val retry = request.url.build().encodedPath.endsWith("/api/sanctum/token/refresh") &&
                    cause !is CancellationException
                if (retry) co.touchlab.kermit.Logger.w(cause) { "Token refresh transport failed; retrying once" }
                retry
            }
            exponentialDelay()
        }
    }

    /**
     * Client for an off-WebView store API ([PsnApi]/[XboxApi]/[EpicApi]/[UbisoftApi]).
     *
     * Deliberately not `expectSuccess`, which also rejects 3xx: the PSN authorize step reads its auth
     * code out of a 302 `Location`. Only 4xx/5xx are errors — but they must be errors, because without
     * a validator the error body reaches the JSON parser and a 401 silently reads as "no games".
     */
    private fun storeApiClient(configure: HttpClientConfig<*>.() -> Unit = {}) = HttpClient {
        ktorHttpClientConfig()
        HttpResponseValidator {
            validateResponse { response ->
                if (response.status.value >= HttpStatusCode.BadRequest.value) {
                    throw ResponseException(response, response.bodyAsText())
                }
            }
        }
        configure()
    }

    // PSN API client: must NOT follow redirects (the OAuth code is read from the authorizing 302).
    @Single
    fun providePsnApi() = PsnApi(storeApiClient { followRedirects = false })

    // Xbox Live API client (token chain + titlehub); plain JSON calls, the MSA token comes from the WebView.
    @Single
    fun provideXboxApi() = XboxApi(storeApiClient())

    // Epic launcher API client (token exchange + library/catalog); plain JSON, the auth code comes from
    // the WebView. Off-WebView so it isn't CORS-blocked like the same calls would be in the browser.
    @Single
    fun provideEpicApi() = EpicApi(storeApiClient())

    // Ubisoft Connect API client (GraphQL owned games); plain JSON, the session ticket comes from the
    // WebView. Off-WebView so it isn't CORS-blocked like the same calls would be in the browser.
    @Single
    fun provideUbisoftApi() = UbisoftApi(storeApiClient())
}
